import React, { useState, useEffect } from 'react';
import { FaUser, FaEnvelope, FaLock, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import {
  SettingsPanel,
  SettingsRow,
  SettingsNote,
  SettingsSectionHeading,
  SettingsSaveStatus,
} from './primitives';
import './AccountSettings.scss';

interface AccountData {
  email: string;
  username: string;
}

export const AccountSettings: React.FC = () => {
  const navigate = useNavigate();

  const [accountData, setAccountData] = useState<AccountData>({
    email: '',
    username: '',
  });

  const [passwordForEmail, setPasswordForEmail] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SettingsSaveStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadAccountData();
  }, []);

  const loadAccountData = async () => {
    try {
      const response = await api.get('/user/account');
      setAccountData({
        email: response.data.email || '',
        username: response.data.username || '',
      });
    } catch (error) {
      console.error('Failed to load account data:', error);
    }
  };

  const handleEmailChange = async () => {
    if (!accountData.email.trim()) {
      setErrorMessage('Email cannot be empty');
      setSaveStatus('error');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(accountData.email)) {
      setErrorMessage('Please enter a valid email address');
      setSaveStatus('error');
      return;
    }
    if (!showPasswordInput) {
      setShowPasswordInput(true);
      return;
    }
    if (!passwordForEmail) {
      setErrorMessage('Password is required to change email');
      setSaveStatus('error');
      return;
    }
    setIsLoading(true);
    setSaveStatus('saving');
    setErrorMessage('');
    try {
      await api.patch('/user/account/email', {
        email: accountData.email.trim(),
        password: passwordForEmail,
      });
      setSaveStatus('saved');
      setPasswordForEmail('');
      setShowPasswordInput(false);
      toast.success('Email updated');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error: any) {
      console.error('Failed to update email:', error);
      const msg = error.response?.data?.error || 'Failed to update email';
      setErrorMessage(msg);
      setSaveStatus('error');
      toast.error(msg);
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsPanel
      title="Account Settings"
      description="Manage your account details and preferences"
    >
      <div className="account-settings-toolbar">
        <button
          type="button"
          className="account-settings-toolbar__profile-btn"
          onClick={() => navigate(`/profile/@${accountData.username}`)}
          title="View Profile"
        >
          <FaUser /> View profile
        </button>
      </div>

      {errorMessage && (
        <SettingsNote tone="warning">
          <p>
            <FaExclamationTriangle /> {errorMessage}
          </p>
        </SettingsNote>
      )}

      <SettingsRow
        icon={<FaUser />}
        title="Username"
        description="Your unique username (cannot be changed)"
      >
        <div className="account-settings-inline-input">
          <input type="text" value={accountData.username} disabled readOnly />
        </div>
      </SettingsRow>

      <SettingsRow
        icon={<FaEnvelope />}
        title="Email Address"
        description="Your email for notifications and account recovery"
      >
        <div className="account-settings-inline-input">
          <input
            type="email"
            value={accountData.email}
            onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
            placeholder="Enter email address"
            disabled={isLoading}
          />
          <button
            type="button"
            className={`account-settings-inline-btn account-settings-inline-btn--${saveStatus}`}
            onClick={handleEmailChange}
            disabled={isLoading || !accountData.email.trim()}
          >
            {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? '✓ Saved' : 'Update'}
          </button>
        </div>
        {showPasswordInput && (
          <div className="account-settings-password-verify">
            <FaLock />
            <input
              type="password"
              value={passwordForEmail}
              onChange={(e) => setPasswordForEmail(e.target.value)}
              placeholder="Enter your password to confirm"
              disabled={isLoading}
            />
            <button type="button" onClick={() => setShowPasswordInput(false)} disabled={isLoading}>
              Cancel
            </button>
          </div>
        )}
      </SettingsRow>

      <SettingsNote tone="info">
        <p>
          <strong>Note:</strong> Changing your email requires password verification.
        </p>
      </SettingsNote>

      <SettingsSectionHeading title="Danger zone" />
      <SettingsRow
        icon={<FaTrash />}
        title="Delete Account"
        description="Permanently delete your account and all associated data"
        disabled
        control={
          <button type="button" className="account-settings-danger-btn" disabled>
            <FaTrash /> Delete account
          </button>
        }
      />
      <SettingsNote tone="warning">
        <p>
          <strong>Coming soon:</strong> Account deletion will be available in a future update.
        </p>
      </SettingsNote>
    </SettingsPanel>
  );
};

export default AccountSettings;
